/**
 * StatusChip — Roomgrid status pill (`.rg-pill`).
 *
 * Radius 99, 11px/700, with a leading 5px dot in currentColor. Colors come
 * from the Roomgrid semantic triplets (bg + fg), per the handoff mapping:
 *
 *   delivered/ok → ok      failed/error → danger    pending/queued → warn
 *   scheduled/read → info  disabled/unknown → neutral
 *
 * Use inside a react-admin field via <FunctionField> or standalone.
 */

import React from "react";
import { Box, useTheme } from "@mui/material";
import { RaRecord, useRecordContext, useTranslate } from "react-admin";
import type { RgTriplet } from "@/roomgrid";

type TripletKey = "ok" | "danger" | "warn" | "info" | "neutral" | "violet";

// Normalised status token -> semantic triplet. Extend here as the backend
// adds statuses; unknown values fall through to neutral.
const STATUS_TRIPLET: Record<string, TripletKey> = {
  delivered: "ok",
  ok: "ok",
  success: "ok",
  accepted: "ok",
  active: "ok",
  sent: "ok",
  read: "info",
  seen: "info",
  opened: "info",
  scheduled: "info",
  trial: "info",
  queued: "warn",
  pending: "warn",
  processing: "warn",
  submitted: "warn",
  failed: "danger",
  error: "danger",
  bounced: "danger",
  rejected: "danger",
  undelivered: "danger",
  suspended: "danger",
  blocked: "danger",
  limited: "warn",
  disabled: "neutral",
  inactive: "neutral",
};

const normalise = (value: unknown): string =>
  String(value ?? "")
    .trim()
    .toLowerCase();

export interface StatusChipProps {
  /** Record field to read the status from (default: "status"). */
  source?: string;
  /** Explicit value; overrides `source` when provided. */
  value?: string;
  /** i18n prefix used to translate the label, e.g. "resources.x.status". */
  translatePrefix?: string;
  /** Force a triplet regardless of the value mapping. */
  triplet?: TripletKey;
  /**
   * Record injected by the datagrid. The desktop grid supplies it through
   * RecordContext; the mobile card clones children with a `record` prop.
   */
  record?: RaRecord;
  sx?: object;
  /** Kept for call-site compatibility with the previous Chip-based API. */
  size?: "small" | "medium";
}

const StatusChip: React.FC<StatusChipProps> = ({
  source = "status",
  value,
  translatePrefix,
  triplet,
  record: recordProp,
  sx,
}) => {
  const recordFromContext = useRecordContext();
  const record = recordProp ?? recordFromContext;
  const translate = useTranslate();
  const theme = useTheme();
  const rg = theme.custom.rg;

  const raw = value ?? (record ? (record as any)[source] : undefined);
  const key = normalise(raw);
  if (!key) return null;

  const tripletKey: TripletKey = triplet ?? STATUS_TRIPLET[key] ?? "neutral";
  const colors: RgTriplet = rg[tripletKey];
  const label = translatePrefix
    ? translate(`${translatePrefix}.${key}`, { _: String(raw) })
    : String(raw);

  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        height: 22,
        px: "10px",
        borderRadius: "99px",
        backgroundColor: colors.bg,
        color: colors.fg,
        fontSize: "11px",
        fontWeight: 700,
        lineHeight: 1,
        whiteSpace: "nowrap",
        ...sx,
      }}
    >
      <Box
        component="span"
        sx={{
          width: 5,
          height: 5,
          borderRadius: "99px",
          backgroundColor: "currentColor",
          flex: "none",
        }}
      />
      {label}
    </Box>
  );
};

export default StatusChip;
